<template>
  <div>
    <Head :title="`Feedback - ${feedback.subject_name}`" />
    <div class="mb-8">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div class="bg-gray-50 rounded-lg py-2 px-3 border border-gray-200">
          <p class="text-xs font-semibold text-gray-600 uppercase">User</p>
          <p class="text-lg text-gray-800">
            {{ feedback.user_name }}
          </p>
        </div>
        <div class="bg-gray-50 rounded-lg py-2 px-3 border border-gray-200">
          <p class="text-xs font-semibold text-gray-600 uppercase">Curs</p>
          <p class="text-lg text-gray-800">
            {{ feedback.course_name }}
          </p>
        </div>
        <div class="bg-gray-50 rounded-lg py-2 px-3 border border-gray-200">
          <p class="text-xs font-semibold text-gray-600 uppercase">Subiect</p>
          <p class="text-lg text-gray-800">
            {{ feedback.subject_name }}
          </p>
        </div>
      </div>
    </div>

    <!-- Files Section -->
    <div class="bg-white rounded-md shadow p-6 mb-8">
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-lg font-semibold">Fisiere</h2>
        <div class="flex gap-2">
          <Button
            :severity="viewMode === 'list' ? 'primary' : 'secondary'"
            size="small"
            icon="pi pi-list"
            label="List"
            @click="viewMode = 'list'"
          />
          <Button
            :severity="viewMode === 'grid' ? 'primary' : 'secondary'"
            size="small"
            icon="pi pi-th-large"
            label="Grid"
            @click="viewMode = 'grid'"
          />
        </div>
      </div>
      <div v-if="feedback.file_groups.length > 0">
        <template v-for="(group, idx) in feedback.file_groups" :key="idx">
          <Panel
            toggleable
            :collapsed="false"
            :pt="panelPt(group)"
            class="mb-4"
          >
            <template #header>
              <div class="flex items-center justify-between w-full">
                <span>
                  {{
                    group.type === 'uploader'
                      ? 'Fisiere incarcate de utilizator'
                      : 'Fisiere feedback'
                  }}
                </span>
                <span class="text-sm text-slate-600">
                  {{ groupDateInterval(group) }}
                </span>
              </div>
            </template>
            <!-- List View -->
            <div v-if="viewMode === 'list'" class="space-y-2">
              <template v-for="file in group.files" :key="file.id">
                <FileItem :file="file" variant="list" :on-delete="deleteFile" />
              </template>
            </div>
            <!-- Grid View -->
            <div v-else class="flex flex-wrap gap-6">
              <template v-for="file in group.files" :key="file.id">
                <FileItem :file="file" variant="grid" />
              </template>
            </div>
          </Panel>
        </template>
      </div>
      <div v-else class="text-center text-slate-600">
        <p>Nu sunt fisiere incarcate inca.</p>
      </div>
    </div>

    <!-- Admin File Upload Section -->
    <div class="bg-white rounded-md shadow p-6 mb-8">
      <h2 class="text-lg font-semibold mb-4">Incarca fisiere feedback</h2>
      <FileUploader ref="fileUploader" v-model:files="uploadedFiles" />
      <div
        v-if="uploadError"
        class="mt-3 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm"
      >
        {{ uploadError }}
      </div>
      <button
        v-if="uploadedFiles.length > 0"
        class="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        :disabled="loading"
        @click="submitFiles"
      >
        {{
          loading
            ? 'Se incarca...'
            : `Salveaza ${uploadedFiles.length} fisier(e)`
        }}
      </button>
    </div>

    <!-- Completion Section -->
    <div class="bg-white rounded-md shadow p-6">
      <div class="flex items-center justify-between">
        <div>
          <h2 class="text-lg font-semibold">Status</h2>
          <p class="text-gray-600">
            {{
              feedback.completed
                ? 'Feedback finalizat'
                : 'Feedback in asteptare'
            }}
          </p>
        </div>
        <button
          class="px-4 py-2 rounded transition-colors"
          :class="
            feedback.completed
              ? 'bg-gray-600 text-white hover:bg-gray-700'
              : 'bg-green-600 text-white hover:bg-green-700'
          "
          :disabled="loading"
          @click="toggleCompletion"
        >
          {{
            feedback.completed ? 'Marcheaza nefinalizat' : 'Marcheaza finalizat'
          }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { Head } from '@inertiajs/vue3';
import Layout from '@/Layouts/Main.vue';
import FileUploader from '@/Shared/FileUploader.vue';
import FileItem from '@/Shared/FileItem.vue';
import Button from 'primevue/button';
import Panel from 'primevue/panel';
import axios from 'axios';

export default {
  components: {
    Head,
    FileUploader,
    FileItem,
    Button,
    Panel,
  },
  layout: Layout,
  props: {
    feedback: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      uploadedFiles: [],
      loading: false,
      viewMode: 'list',
      pollInterval: null,
      uploadError: null,
    };
  },

  watch: {
    'feedback.file_groups': {
      deep: true,
      handler() {
        if (this.hasPendingFiles()) {
          if (!this.pollInterval) {
            this.startPolling();
          }
        } else {
          this.stopPolling();
        }
      },
    },
  },

  mounted() {
    this.startPolling();
  },

  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    panelPt(group) {
      if (group.type !== 'feedback') return undefined;

      return {
        root: { class: 'bg-blue-50' },
        content: { class: 'bg-blue-50' },
      };
    },
    groupDateInterval(group) {
      if (!group.files || group.files.length === 0) return '';

      const dates = group.files
        .map((f) => new Date(f.created_at))
        .filter((d) => !Number.isNaN(d.getTime()))
        .sort((a, b) => a - b);

      if (dates.length === 0) return '';

      const format = (d) =>
        d.toLocaleDateString('ro-RO', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        });

      const first = dates[0];
      const last = dates[dates.length - 1];

      if (first.toDateString() === last.toDateString()) {
        return format(first);
      }

      return `${format(first)} — ${format(last)}`;
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString('ro-RO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    async submitFiles() {
      if (this.uploadedFiles.length === 0) return;

      this.loading = true;
      this.uploadError = null;
      const formData = new FormData();

      this.uploadedFiles.forEach((file) => {
        formData.append('feedback_files[]', file);
      });
      formData.append('format', 'json');

      try {
        await axios.patch(this.$routes.feedback(this.feedback.id), formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        this.uploadedFiles = [];
        this.$refs.fileUploader.$refs.fileUpload.clear();
        this.$inertia.reload();
      } catch (error) {
        console.error('Error uploading files:', error);
        this.uploadError =
          error.response?.data?.error || 'Eroare la incarcarea fisierelor';
      } finally {
        this.loading = false;
      }
    },
    toggleCompletion() {
      this.loading = true;
      this.$inertia.patch(
        this.$routes.feedback(this.feedback.id),
        { feedback: { completed: !this.feedback.completed } },
        {
          onFinish: () => {
            this.loading = false;
          },
        },
      );
    },
    hasPendingFiles() {
      if (!this.feedback.file_groups) {
        return false;
      }
      return this.feedback.file_groups.some((group) =>
        group.files.some((file) => !file.scanned),
      );
    },
    startPolling() {
      if (this.hasPendingFiles()) {
        this.pollInterval = setInterval(() => {
          this.$inertia.reload();
        }, 3000);
      }
    },
    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    },
    async deleteFile(file) {
      const name = file?.filename || 'acest fisier';
      if (!confirm(`Esti sigur(a) ca vrei sa stergi fisierul "${name}"?`))
        return;

      try {
        await axios.delete(
          this.$routes.feedback_scannable_file(this.feedback.id, file.id),
        );
        this.$inertia.reload();
      } catch (error) {
        console.error('Error deleting file:', error);
      }
    },
  },
};
</script>
